import { useEffect, useMemo, useState } from "react";

import {
  CLUSTER_PALETTE,
  type PortfolioClusterKey,
} from "./portfolioClusterStyles";
import RelationshipPriorityMatrix from "./RelationshipPriorityMatrix";
import {
  DEFAULT_PRIORITY_SCORING_WEIGHTS,
  QUADRANT_LABEL,
  scorePairs,
  TIER_COLOR,
  TIER_LABEL,
  TIER_ORDER,
  type PriorityQuadrant,
  type PriorityScoringWeights,
  type PriorityTier,
  type ScoredPair,
} from "./relationshipPriorityScoring";
import CommonModal from "../../Components/Modal";
import { useAppDispatch } from "../../Store/Hooks";
import type { RelationshipPairRow } from "../../Store/Reducers/MyPortfolioOverviewSlice";
import { fetchVendorConfigRaw } from "../../Store/Reducers/VendorAdminSlice";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import { normalizePriorityScoringWeights } from "../Settings/PriorityScoringWeightsForm";

/** mock `.prio-tier-vh/h/m/l` (CSS ~648-651). */
const TIER_BADGE_STYLE: Record<PriorityTier, string> = {
  vh: "bg-red-50 text-red-600 border border-red-200",
  h: "bg-amber-50 text-amber-600 border border-amber-200",
  m: "bg-purple-50 text-purple-600 border border-purple-200",
  l: "bg-gray-50 text-gray-500 border border-gray-200",
};

/** mock `.prio-section-hdr.vh/h/m/l` (CSS ~665-668). */
const SECTION_HDR_COLOR: Record<PriorityTier, string> = {
  vh: "#dc2626",
  h: "#d97706",
  m: "#9333EA",
  l: "#9ca3af",
};

function clusterKey(raw: string | null): PortfolioClusterKey {
  const k = (raw || "").toLowerCase();
  if (k === "triage" || k === "defend" || k === "grow" || k === "expand") {
    return k;
  }
  return "unscored";
}

/** mock `.pair-card.prioritized-view` (HTML ~3298-3471). */
function PriorityCard({ sp }: { sp: ScoredPair }) {
  const { pair, score, tier } = sp;
  const cluster = clusterKey(pair.account_cluster);
  const clusterColor = CLUSTER_PALETTE[cluster].color;
  const rlUnknown = pair.rl_current == null;

  return (
    <div className="mb-2.5 rounded-[10px] border border-gray-200 bg-white p-3.5">
      <div className="mb-[3px] flex items-start justify-between gap-2">
        <div className="text-[13.5px] font-bold text-gray-900">
          {pair.sponsor_name ? `${pair.sponsor_name} ↔ ` : ""}
          {pair.contact_title || "Contact"} ({pair.contact_name})
        </div>
        <div className="flex shrink-0 items-center gap-[5px]">
          <span
            className={`rounded px-2 py-[2px] text-[10px] font-extrabold uppercase tracking-wide ${
              tier === "unassessed"
                ? "border border-gray-200 bg-gray-50 text-gray-500"
                : TIER_BADGE_STYLE[tier as PriorityTier]
            }`}
          >
            {tier === "unassessed" ? "Unassessed" : TIER_LABEL[tier]}
          </span>
          {score != null && (
            <span className="whitespace-nowrap rounded border border-gray-200 bg-gray-50 px-[7px] py-[2px] text-[11px] font-extrabold text-gray-500">
              {score}
            </span>
          )}
        </div>
      </div>
      <div className="mt-[2px] text-xs text-gray-500">
        {pair.account_name}
        {" · "}
        <span className="font-bold" style={{ color: clusterColor }}>
          {CLUSTER_PALETTE[cluster].label}
        </span>
        {pair.account_region ? ` · ${pair.account_region}` : ""}
      </div>
      <div className="mt-[7px] flex flex-wrap items-center gap-1.5">
        {pair.account_renewal_days != null &&
          pair.account_renewal_days < 180 && (
            <span className="rounded bg-red-50 px-2 py-[2px] text-[10.5px] font-bold text-red-600">
              Renewal in {pair.account_renewal_days}d
            </span>
          )}
        {rlUnknown ? (
          <span
            className="rounded border border-gray-200 bg-gray-50 px-[7px] py-[2px] font-mono text-[10.5px] text-gray-400"
            title="Unassessed"
          >
            RL ?
          </span>
        ) : pair.rl_goal != null ? (
          <span className="rounded border border-gray-200 bg-gray-50 px-[7px] py-[2px] font-mono text-[10.5px] text-gray-500">
            RL {pair.rl_current} → Goal RL {pair.rl_goal}
          </span>
        ) : (
          <span className="rounded bg-purple-50 px-2 py-[2px] text-[10.5px] font-bold text-purple-600">
            No RL goal set
          </span>
        )}
      </div>
    </div>
  );
}

interface Props {
  pairs: RelationshipPairRow[];
  onBackToHealth: () => void;
  prioritizedAt: Date | null;
}

export default function RelationshipPrioritizedView({
  pairs,
  onBackToHealth,
  prioritizedAt,
}: Props) {
  const dispatch = useAppDispatch();
  const [view, setView] = useState<"list" | "matrix">("list");
  const [legendOpen, setLegendOpen] = useState(false);
  const [activeQuadrant, setActiveQuadrant] = useState<
    PriorityQuadrant | "all"
  >("all");
  const [selectedBubblePairId, setSelectedBubblePairId] = useState<
    string | null
  >(null);
  const [weights, setWeights] = useState<PriorityScoringWeights>(
    DEFAULT_PRIORITY_SCORING_WEIGHTS
  );

  // Org-level Priority Scoring Weights (Portfolio Settings modal) — fetched
  // once and applied to every score computed below. Without this, the
  // settings panel would save values that never influence what the user
  // sees, which is exactly the gap a prior audit pass found.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await dispatch(
          fetchVendorConfigRaw(API_URLS.fetchPriorityScoringWeightsConfig())
        ).unwrap();
        const status = (response as { status?: string })?.status;
        if (status === "failure" || status === "unauthorized") return;
        const record = response as {
          value?: unknown;
          content?: { value?: unknown };
        };
        const raw = record?.value ?? record?.content?.value;
        if (!cancelled) setWeights(normalizePriorityScoringWeights(raw));
      } catch {
        // best-effort — defaults already reproduce the original scoring
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const scored = useMemo(() => scorePairs(pairs, weights), [pairs, weights]);
  const scoredOnly = scored.filter(s => s.tier !== "unassessed");
  const unassessed = scored.filter(s => s.tier === "unassessed");

  const byTier = useMemo(() => {
    const map: Record<PriorityTier, ScoredPair[]> = {
      vh: [],
      h: [],
      m: [],
      l: [],
    };
    for (const s of scoredOnly) map[s.tier as PriorityTier].push(s);
    for (const tier of TIER_ORDER) {
      map[tier].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    }
    return map;
  }, [scoredOnly]);

  const matrixPairs = scored.filter(
    s => activeQuadrant === "all" || s.quadrant === activeQuadrant
  );

  const daysAgo = prioritizedAt
    ? Math.floor((Date.now() - prioritizedAt.getTime()) / 86400000)
    : 0;

  return (
    <div>
      {/* mock `.prio-view-hdr` (CSS ~1005-1009) */}
      <div className="mb-3.5 flex flex-wrap items-center gap-3 border-b-2 border-brand pb-3.5 pt-2.5">
        <div>
          <div className="text-[13px] font-extrabold text-gray-900">
            Priority List — {scoredOnly.length + unassessed.length}{" "}
            relationships ranked by urgency
          </div>
          <div className="text-xs text-gray-500">
            Prioritized {daysAgo === 0 ? "today" : `${daysAgo} days ago`} ·
            Active for 4 weeks
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2.5">
          <div className="flex overflow-hidden rounded-md border border-gray-200 bg-gray-50 p-[3px]">
            <button
              type="button"
              onClick={() => setView("list")}
              className={`rounded px-3 py-1 text-xs font-semibold ${
                view === "list"
                  ? "bg-white text-brand shadow-sm"
                  : "text-gray-500"
              }`}
            >
              List
            </button>
            <button
              type="button"
              onClick={() => setView("matrix")}
              className={`rounded px-3 py-1 text-xs font-semibold ${
                view === "matrix"
                  ? "bg-white text-brand shadow-sm"
                  : "text-gray-500"
              }`}
            >
              Matrix
            </button>
          </div>
          <button
            type="button"
            onClick={onBackToHealth}
            className="rounded-md border border-gray-200 bg-gray-50 px-3.5 py-1.5 text-xs font-semibold text-gray-500 hover:border-brand hover:text-brand"
          >
            ← Back to health view
          </button>
        </div>
      </div>

      {legendOpen && (
        <div className="mb-3.5 rounded-lg border border-gray-200 bg-white px-4 py-3 text-xs text-gray-500">
          <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500">
            Priority Score — How it&apos;s calculated
          </div>
          <div className="flex flex-wrap gap-2.5">
            {[
              [
                "Account cluster",
                "Triage=high, Defend=med, Grow/Expand=low base weight",
              ],
              ["RL gap from goal", "Larger gap → higher urgency to move"],
              [
                "Renewal proximity",
                "Days to renewal, exponentially weighted inside 90d",
              ],
              ["Signal recency", "Days since last assessment or mapping"],
              [
                "Contact seniority",
                "C-suite / EVP-SVP / VP / Director weighted higher",
              ],
            ].map(([label, desc]) => (
              <div
                key={label}
                className="rounded bg-gray-50 px-2.5 py-1.5 text-[11.5px]"
              >
                <span className="font-bold text-gray-900">{label}</span>
                <br />
                {desc}
              </div>
            ))}
          </div>
        </div>
      )}

      {view === "list" ? (
        <div>
          {unassessed.length > 0 && (
            <div>
              <div
                className="mb-2.5 flex items-center gap-2 border-b border-gray-200 pb-[7px] pt-1 text-[10px] font-extrabold uppercase tracking-wide"
                style={{ color: "#9ca3af" }}
              >
                Assessment Needed
                <span className="text-[10px] font-semibold text-gray-400">
                  {unassessed.length} relationship
                  {unassessed.length === 1 ? "" : "s"}
                </span>
              </div>
              {unassessed.map(sp => (
                <PriorityCard key={sp.pair.pair_id} sp={sp} />
              ))}
            </div>
          )}
          {TIER_ORDER.map(tier => {
            const list = byTier[tier];
            if (list.length === 0) return null;
            return (
              <div key={tier}>
                <div
                  className="mb-2.5 flex items-center gap-2 border-b border-gray-200 pb-[7px] pt-3.5 text-[10px] font-extrabold uppercase tracking-wide first:pt-1"
                  style={{ color: SECTION_HDR_COLOR[tier] }}
                >
                  {TIER_LABEL[tier]}
                  <span className="text-[10px] font-semibold text-gray-400">
                    {list.length} relationship{list.length === 1 ? "" : "s"}
                  </span>
                </div>
                {list.map(sp => (
                  <PriorityCard key={sp.pair.pair_id} sp={sp} />
                ))}
              </div>
            );
          })}
          <div className="flex items-center justify-between px-1 py-2.5 text-[11.5px] text-gray-500">
            <span>
              Showing {scoredOnly.length + unassessed.length} priority
              relationships · Sorted by urgency score
            </span>
            <button
              type="button"
              onClick={() => setLegendOpen(o => !o)}
              className="border-none bg-transparent p-0 text-[11.5px] font-bold text-brand"
            >
              Score logic ↗
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="mb-2.5 flex flex-wrap gap-1.5">
            {(
              ["all", "dofirst", "build", "fasttrack", "maintain"] as const
            ).map(q => (
              <button
                key={q}
                type="button"
                onClick={() => setActiveQuadrant(q)}
                className={`rounded-full border px-3 py-1 text-[11.5px] font-semibold ${
                  activeQuadrant === q
                    ? "border-brand bg-brand text-white"
                    : "border-gray-200 bg-gray-50 text-gray-500"
                }`}
              >
                {q === "all" ? "All" : QUADRANT_LABEL[q]}
              </button>
            ))}
          </div>
          <RelationshipPriorityMatrix
            pairs={scored}
            activeQuadrant={activeQuadrant}
            onQuadrantClick={q => setActiveQuadrant(q)}
            onBubbleClick={pairId =>
              setSelectedBubblePairId(prev => (prev === pairId ? null : pairId))
            }
          />
          {selectedBubblePairId ? (
            (() => {
              const sp = scored.find(
                s => s.pair.pair_id === selectedBubblePairId
              );
              return sp ? (
                <div className="mt-3.5">
                  <PriorityCard sp={sp} />
                </div>
              ) : null;
            })()
          ) : activeQuadrant !== "all" && matrixPairs.length > 0 ? (
            <div className="mt-3.5">
              {matrixPairs
                .filter(s => s.tier !== "unassessed")
                .map(sp => (
                  <PriorityCard key={sp.pair.pair_id} sp={sp} />
                ))}
            </div>
          ) : null}
          <div className="px-1 py-2.5 text-[11.5px] text-gray-500">
            Urgency × Importance matrix · Click a bubble or quadrant label to
            filter
          </div>
        </div>
      )}
    </div>
  );
}

interface PfmConfirmModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/** mock `.pfm-overlay`/`.pfm-dialog` (HTML ~3880-3889, CSS ~1020-1033). */
export function PfmConfirmModal({
  isOpen,
  onCancel,
  onConfirm,
}: PfmConfirmModalProps) {
  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onCancel}
      showHeader={false}
      size="sm"
      containerClassName="!max-w-[420px] !rounded-xl !p-7"
    >
      <h3 className="m-0 mb-2.5 text-base font-extrabold text-gray-900">
        Generate your priority list?
      </h3>
      <p className="m-0 mb-5 text-[13px] leading-relaxed text-gray-500">
        This will compute urgency scores across all your mapped relationships
        and generate a ranked priority list. Your list will stay active for{" "}
        <strong>4 weeks</strong> (configurable in Settings). You can
        re-prioritize at any time.
      </p>
      <div className="flex justify-end gap-2.5">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-200 bg-gray-50 px-4.5 py-2 text-[13px] font-semibold text-gray-500"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="rounded-lg border-none bg-brand px-4.5 py-2 text-[13px] font-bold text-white"
        >
          Generate priority list
        </button>
      </div>
    </CommonModal>
  );
}

/** tier color re-export for the trigger-button badge in the parent tab. */
export { TIER_COLOR };
